import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { KickoffWizard } from '../components/wedding-kickoff/KickoffWizard';
import { HeartIcon, UsersIcon, ChevronDownIcon, ChevronUpIcon, GlobeIcon, CheckCircle2Icon } from 'lucide-react';
export function WeddingKickoff() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const faqs = [{
    q: 'Is this only for people attending the wedding?',
    a: 'Not at all. Desta Pass is for everyone — those flying out AND those celebrating from home. Everyone gets the full experience.'
  }, {
    q: 'Do parents need to be tech-savvy?',
    a: "No. We designed this to be as simple as WhatsApp. Pick your wedding, add names and numbers, and you're done. That's it."
  }, {
    q: 'Can we add more guests later?',
    a: "Yes! Start with a few people today and keep adding as the wedding gets closer. There's no limit."
  }, {
    q: 'Is everything private?',
    a: 'Completely. Only the people you invite can see your wedding community, photos, and updates. Nothing is public.'
  }, {
    q: 'What if I only have phone numbers, not emails?',
    a: "Phone numbers are all you need. You can copy them straight from WhatsApp — we'll show you how."
  }];
  return <div className="bg-[#FAFAF9] min-h-screen font-sans" data-id="element-3592">
      {/* ─── HERO ─── */}
      <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden" data-id="element-3593">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-50/80 via-rose-50/30 to-[#FAFAF9] -z-10" data-id="element-3594" />
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-gradient-to-bl from-rose-200/40 to-amber-200/40 blur-3xl -z-10" data-id="element-3595" />

        <div className="container mx-auto px-4 md:px-6" data-id="element-3596">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-3597">
            <div className="text-center lg:text-left" data-id="element-3598">
              <div className="inline-flex items-center px-4 py-2 bg-white/60 backdrop-blur-sm border border-amber-200/60 rounded-full text-amber-800 text-sm font-medium mb-6 shadow-sm" data-id="element-3599">
                <HeartIcon className="h-4 w-4 mr-2 text-rose-500" data-id="element-3600" />
                Destination Wedding? We've got everyone covered.
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight" data-id="element-3601">
                Give every guest{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-amber-600" data-id="element-3602">
                  the wedding experience of a lifetime.
                </span>
              </h1>

              <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-id="element-3603">
                Whether they're on the beach or watching from home — Desta Pass
                makes sure everyone feels included, informed, and part of every
                beautiful moment.
              </p>

              <Button variant="primary" size="lg" onClick={() => document.getElementById('kickoff-wizard')?.scrollIntoView({
              behavior: 'smooth'
            })} className="w-full sm:w-auto !bg-gradient-to-r !from-rose-500 !to-amber-500 !text-white hover:!from-rose-600 hover:!to-amber-600 border-0 shadow-xl shadow-rose-200/50 font-semibold px-8 py-4 text-lg rounded-full" data-id="element-3604">
                Start Inviting Your Family
              </Button>
            </div>

            <div className="relative mt-8 lg:mt-0" data-id="element-3605">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-rose-200/30" data-id="element-3606">
                <img src="https://cdn.magicpatterns.com/uploads/457E1jH9TbRt569mzs6f3b/destapassweddingkickoff.jpg" alt="South Asian destination wedding with live virtual guests on a big screen alongside in-person attendees on the beach" className="w-full h-[400px] md:h-[520px] object-cover" data-id="element-3607" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" data-id="element-3608" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── EVERYONE BENEFITS ─── */}
      <section className="py-20 bg-white" data-id="element-3609">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3610">
          <div className="max-w-5xl mx-auto" data-id="element-3611">
            <div className="text-center mb-14" data-id="element-3612">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-3613">
                One app. Two experiences. Everyone wins.
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-3614">
                Desta Pass works for the people who are there — and the people
                who wish they could be.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8" data-id="element-3615">
              {/* Attending */}
              <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-3xl p-8 border border-amber-200" data-id="element-3616">
                <div className="inline-flex items-center px-4 py-1.5 bg-amber-100 rounded-full text-amber-800 text-sm font-semibold mb-6" data-id="element-3617">
                  ✈️ If you're attending
                </div>
                <div className="space-y-5" data-id="element-3618">
                  {[{
                  emoji: '📅',
                  text: 'Full event schedule with times, dress codes, and venues'
                }, {
                  emoji: '🗺️',
                  text: 'Resort maps so you never feel lost'
                }, {
                  emoji: '📸',
                  text: "Shared photo album — everyone's moments in one place"
                }, {
                  emoji: '👋',
                  text: 'Meet the other family before you even arrive'
                }, {
                  emoji: '🍽️',
                  text: 'Food menus with dietary info (Veg, Jain, Halal)'
                }, {
                  emoji: '🆘',
                  text: 'Emergency contacts and concierge help on your phone'
                }].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3619">
                      <span className="text-xl flex-shrink-0" data-id="element-3620">
                        {item.emoji}
                      </span>
                      <span className="text-gray-700 text-[15px]" data-id="element-3621">
                        {item.text}
                      </span>
                    </div>)}
                </div>
              </div>

              {/* Celebrating from home */}
              <div className="bg-gradient-to-br from-rose-50 to-amber-50 rounded-3xl p-8 border border-rose-200" data-id="element-3622">
                <div className="inline-flex items-center px-4 py-1.5 bg-rose-100 rounded-full text-rose-800 text-sm font-semibold mb-6" data-id="element-3623">
                  🏠 If you're celebrating from home
                </div>
                <div className="space-y-5" data-id="element-3624">
                  {[{
                  emoji: '📱',
                  text: 'Live updates and photos as the wedding happens'
                }, {
                  emoji: '💬',
                  text: 'Send blessings and messages to the couple and family'
                }, {
                  emoji: '🎥',
                  text: 'Watch ceremonies and key moments in real time'
                }, {
                  emoji: '👨‍👩‍👧‍👦',
                  text: 'Stay connected with both families in one private space'
                }, {
                  emoji: '🖼️',
                  text: 'See every photo and video — not just what makes social media'
                }, {
                  emoji: '❤️',
                  text: 'Feel like you were there, even from your living room'
                }].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3625">
                      <span className="text-xl flex-shrink-0" data-id="element-3626">
                        {item.emoji}
                      </span>
                      <span className="text-gray-700 text-[15px]" data-id="element-3627">
                        {item.text}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SIMPLE MESSAGE FOR PARENTS ─── */}
      <section className="py-16 bg-[#FAFAF9]" data-id="element-3628">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3629">
          <div className="max-w-3xl mx-auto text-center" data-id="element-3630">
            <div className="bg-white rounded-3xl p-8 md:p-12 border border-amber-100 shadow-sm" data-id="element-3631">
              <div className="text-4xl mb-4" data-id="element-3632">🙏</div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-3633">
                Parents — this is for you.
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-3" data-id="element-3634">
                We know how important it is that everyone feels included. Your
                extended family, your elders, your lifelong friends — they all
                matter.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-3635">
                The Kickoff Wizard below is designed to be{' '}
                <strong data-id="element-3636">as simple as sending a WhatsApp message</strong>. Pick
                your wedding, add your people, and we take care of the rest.
              </p>
              <div className="flex flex-wrap justify-center gap-3" data-id="element-3637">
                {['No app to download', 'Works with phone numbers', 'Takes 5 minutes', 'Add people anytime'].map((item, i) => <div key={i} className="inline-flex items-center gap-1.5 px-4 py-2 bg-amber-50 rounded-full text-sm font-medium text-amber-800 border border-amber-200/60" data-id="element-3638">
                    <CheckCircle2Icon className="h-4 w-4 text-amber-600" data-id="element-3639" />
                    {item}
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── KICKOFF WIZARD ─── */}
      <section id="kickoff-wizard" className="py-20 bg-[#FAFAF9]" data-id="element-3640">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3641">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-3642">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-3643">
              Start Your Wedding Kickoff
            </h2>
            <p className="text-lg text-gray-600" data-id="element-3644">
              A simple, guided flow to invite your family and launch your
              wedding community.
            </p>
          </div>
          <div className="max-w-5xl mx-auto" data-id="element-3645">
            <KickoffWizard data-id="element-3646" />
          </div>
        </div>
      </section>

      {/* ─── TESTIMONIALS ─── */}
      <section className="py-20 bg-white" data-id="element-3647">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3648">
          <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6" data-id="element-3649">
            <div className="bg-amber-50/50 p-7 rounded-3xl border border-amber-100" data-id="element-3650">
              <div className="text-amber-400 mb-3 text-sm" data-id="element-3651">★★★★★</div>
              <p className="text-gray-700 italic mb-5 text-[15px] leading-relaxed" data-id="element-3652">
                "My grandmother couldn't fly to Mexico, but she saw every
                ceremony live. She said it was the best gift we could have given
                her."
              </p>
              <div className="font-semibold text-gray-900 text-sm" data-id="element-3653">
                — The Couple
              </div>
            </div>
            <div className="bg-amber-50/50 p-7 rounded-3xl border border-amber-100" data-id="element-3654">
              <div className="text-amber-400 mb-3 text-sm" data-id="element-3655">★★★★★</div>
              <p className="text-gray-700 italic mb-5 text-[15px] leading-relaxed" data-id="element-3656">
                "My parents cared so much about including family in India and
                the UK. This made it effortless to bring everyone together."
              </p>
              <div className="font-semibold text-gray-900 text-sm" data-id="element-3657">
                — Bride's Family
              </div>
            </div>
            <div className="bg-amber-50/50 p-7 rounded-3xl border border-amber-100" data-id="element-3658">
              <div className="text-amber-400 mb-3 text-sm" data-id="element-3659">★★★★★</div>
              <p className="text-gray-700 italic mb-5 text-[15px] leading-relaxed" data-id="element-3660">
                "Even from my living room, I felt completely part of the
                wedding. The photos, the updates — I didn't miss a thing."
              </p>
              <div className="font-semibold text-gray-900 text-sm" data-id="element-3661">
                — Extended Relative
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="py-20 bg-[#FAFAF9]" data-id="element-3662">
        <div className="container mx-auto px-4 md:px-6" data-id="element-3663">
          <div className="max-w-2xl mx-auto" data-id="element-3664">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-10 text-center" data-id="element-3665">
              Common Questions
            </h2>
            <div className="space-y-3" data-id="element-3666">
              {faqs.map((faq, index) => <div key={index} className="border border-gray-200 rounded-2xl overflow-hidden bg-white" data-id="element-3667">
                  <button onClick={() => setOpenFaq(openFaq === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-3668">
                    <span className="font-semibold text-gray-900 pr-4" data-id="element-3669">
                      {faq.q}
                    </span>
                    {openFaq === index ? <ChevronUpIcon className="h-5 w-5 text-rose-500 flex-shrink-0" data-id="element-3670" /> : <ChevronDownIcon className="h-5 w-5 text-gray-400 flex-shrink-0" data-id="element-3671" />}
                  </button>
                  {openFaq === index && <div className="px-6 pb-5 text-gray-600 leading-relaxed" data-id="element-3672">
                      {faq.a}
                    </div>}
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ─── CLOSING CTA ─── */}
      <section className="py-20 relative overflow-hidden" data-id="element-3673">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-600 to-amber-600" data-id="element-3674" />
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-3675">
          <div className="max-w-3xl mx-auto text-center text-white" data-id="element-3676">
            <h2 className="text-3xl md:text-4xl font-bold mb-5" data-id="element-3677">
              Every guest deserves the best wedding experience.
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-xl mx-auto" data-id="element-3678">
              Start your Wedding Kickoff and bring everyone into the celebration
              — it only takes a few minutes.
            </p>
            <Button variant="primary" size="lg" onClick={() => document.getElementById('kickoff-wizard')?.scrollIntoView({
            behavior: 'smooth'
          })} className="!bg-white !text-rose-600 hover:!bg-gray-50 border-0 shadow-xl font-bold px-8 py-4 text-lg rounded-full" data-id="element-3679">
              Start Inviting Your Family
            </Button>
          </div>
        </div>
      </section>
    </div>;
}